import type { UseRadioProps } from '@chakra-ui/react';
import { Box, Flex, Stack, Text, useRadio } from '@chakra-ui/react';
import { DiscountCode, MotionBox } from '@time-line/components/common';
import { Refresh } from '@time-line/components/icons';
import { Price } from 'components/cart/Price';
import { FormatPrice } from 'components/localisation/FormatPrice';
import { ProductPriceSaving } from 'components/product/ProductPriceSaving';
import { AnimatePresence } from 'motion/react';
import { PurchaseType } from 'types/product/PurchaseType';
import type { Subscription } from 'types/product/Subscription';
import { getPricePerMonth } from 'utils/getPricePerMonth';

import { ProductConfiguratorBadge } from './ProductConfiguratorBadge';
import { ProductConfiguratorCancellationNote } from './ProductConfiguratorCancellationNote';
import { ProductConfiguratorRadioButton } from './ProductConfiguratorRadioButton';

interface ProductConfiguratorPlanItemProps
  extends Omit<UseRadioProps, 'id'>, Subscription {
  isMostPopular: boolean;
  isBestValue: boolean;
  isClickable?: boolean;
  showSavingsAsAmount?: boolean;
  isDiscountHidden?: boolean;
  discountCodes?: string[];
  purchaseType?: PurchaseType;
}

export function ProductConfiguratorPlanItem({
  title,
  sellingPlan,
  isDisabled,
  price,
  otpPrice,
  compareAtPrice: rawCompareAtPrice,
  servings,
  isMostPopular,
  isBestValue,
  showSavingsAsAmount,
  isDiscountHidden,
  discountCodes,
  purchaseType,
  isClickable = true,
  ...props
}: ProductConfiguratorPlanItemProps) {
  /* For a subscription-only configuration the one-time-purchase price is not a
   * relevant comparison, so use the subscription's own compare-at price, which
   * `updateProductPrices` sets to the pre-discount subscription price when a
   * promo applies. (Without a promo this equals the OTP price, leaving nothing
   * to strike through.) Otherwise (OTP or both options) keep comparing to OTP. */
  const isSubscriptionOnly = purchaseType === PurchaseType.SUB;
  const subscriptionCompareAt =
    rawCompareAtPrice && rawCompareAtPrice.amount !== otpPrice.amount
      ? rawCompareAtPrice
      : undefined;
  const relevantCompareAt = isSubscriptionOnly
    ? subscriptionCompareAt
    : otpPrice;
  const compareAtPrice = isDiscountHidden ? undefined : relevantCompareAt;
  const { getInputProps, getRadioProps } = useRadio(props);
  const input = getInputProps();
  const radio = getRadioProps();

  return (
    <Box
      as={isClickable ? 'label' : 'div'}
      cursor={isClickable ? 'pointer' : undefined}
    >
      <input {...input} disabled={isDisabled} aria-label={title} />
      <Box
        {...radio}
        p="xs"
        borderColor={
          input.checked && !isDisabled && isClickable
            ? 'gray.600'
            : 'blackAlpha.250'
        }
        borderRadius="base"
        transitionProperty="common"
        transitionDuration="slow"
        borderWidth={1}
        position="relative"
        _focusVisible={{
          outline: '1px solid',
          outlineOffset: 2,
        }}
      >
        {isMostPopular && <ProductConfiguratorBadge variant="mostPopular" />}
        {isBestValue && <ProductConfiguratorBadge variant="bestValue" />}
        <Flex
          alignItems="center"
          gap="2xs"
          sx={{
            '--dot-size':
              input.checked && !isDisabled ? 'scale(1)' : 'scale(0)',
          }}
          _hover={
            !isDisabled
              ? {
                  '--dot-size': input.checked ? 'scale(1):' : 'scale(0.5)',
                }
              : undefined
          }
        >
          {isClickable && (
            <ProductConfiguratorRadioButton
              isActive={input.checked as boolean}
            />
          )}
          <Stack spacing="2px" w="full" minW="0" alignSelf="center">
            <Flex
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Text
                size="title2"
                as="span"
                color={isDisabled ? 'blackAlpha.700' : 'gray.600'}
              >
                {title}
              </Text>
              {servings && (
                <FormatPrice
                  size="title2"
                  amount={getPricePerMonth(servings, price.amount)}
                  afterText={
                    <Text as="span" size="detail" fontWeight="500">
                      /mo
                    </Text>
                  }
                />
              )}
            </Flex>
            <Flex
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              gap="4xs"
            >
              {isDisabled && (
                <Text
                  as="span"
                  size="detail"
                  color="blackAlpha.500"
                  fontWeight="medium"
                >
                  Sold out
                </Text>
              )}
              {!isDisabled && compareAtPrice && (
                <ProductPriceSaving
                  compareAtPrice={compareAtPrice}
                  price={price}
                  showAsAmount={showSavingsAsAmount}
                />
              )}
              <Flex
                flexDirection="row"
                alignItems="center"
                gap="4xs"
                minW="0"
                ml="auto"
              >
                {!isDiscountHidden &&
                  discountCodes &&
                  discountCodes.length === 1 && (
                    <DiscountCode size="sm">{discountCodes[0]}</DiscountCode>
                  )}
                <Price
                  gap="5xs"
                  size="detail"
                  subtotalAmount={compareAtPrice}
                  totalAmount={price}
                  direction="row-reverse"
                  showSavingsAsAmount={showSavingsAsAmount}
                />
              </Flex>
            </Flex>
          </Stack>
        </Flex>
        <AnimatePresence initial={false}>
          {input.checked && (
            <MotionBox
              overflow="hidden"
              initial={{ height: 0 }}
              animate={{ height: 'auto', transition: { duration: 0.25 } }}
              exit={{ height: 0, transition: { duration: 0 } }}
            >
              <Stack
                mt="2xs"
                pt="2xs"
                borderColor="blackAlpha.250"
                borderTopWidth={1}
                spacing="5xs"
              >
                <Flex alignItems="center" gap="3xs">
                  <Refresh boxSize="xs" />
                  <Text size="detail">{sellingPlan.title}</Text>
                </Flex>
                <ProductConfiguratorCancellationNote />
              </Stack>
            </MotionBox>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
}
